import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Autocomplete, Checkbox, Alert,
} from '@mui/material'
import { Add, Delete, Devices, AdminPanelSettings, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import {
  useDeviceGroups, useCreateDeviceGroup, useDeleteDeviceGroup,
  useAddDeviceToGroup, useRemoveDeviceFromGroup,
  useAssignGroupToRole, useUnassignGroupFromRole, useGroupDevices, useGroupRoles,
} from '../../hooks/useDeviceGroups'
import { useDevices, Device } from '../../hooks/useDevices'
import { useRoles, Role } from '../../hooks/useUsers'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d
  const o = d as Record<string, T[]> | undefined
  return o?.devices ?? o?.data ?? o?.roles ?? []
}

export default function DeviceGroupsPage() {
  const { t } = useTranslation()
  const { data: groups = [], isLoading } = useDeviceGroups()
  const { data: devicesRaw } = useDevices()
  const { data: rolesRaw } = useRoles()
  const devices = unwrap<Device>(devicesRaw)
  const roles = unwrap<Role>(rolesRaw)
  const createGroup = useCreateDeviceGroup()
  const deleteGroup = useDeleteDeviceGroup()

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', description: '', color: '#34A8E0' })
  const [manageGroup, setManageGroup] = useState<string | null>(null)

  const handleCreate = async () => {
    try {
      await createGroup.mutateAsync(form)
      analytics.track(EVENTS.DEVICE_GROUP_CREATED, { group_name: form.name })
      setCreateOpen(false)
      setForm({ name: '', description: '', color: '#34A8E0' })
    } catch {
      // Error state is handled by mutation's isError flag
    }
  }

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <PageHeader title={t('deviceGroups.title')} subtitle={t('deviceGroups.subtitle', { count: groups.length })}
        action={<Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>{t('deviceGroups.create')}</Button>}
      />

      {(createGroup.isError || deleteGroup.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('common.error')}</Alert>
      )}

      <Stack spacing={2}>
        {groups.length === 0 && (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <Devices sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body2" color="#64748b">{t('deviceGroups.noGroups')}</Typography>
          </Card>
        )}
        {groups.map((g) => (
          <Card key={g.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: g.color }} />
                <Box>
                  <Typography variant="body1" fontWeight={700}>{g.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{g.description || t('deviceGroups.noDescription')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip icon={<Devices sx={{ fontSize: 14 }} />} label={t('deviceGroups.deviceCount', { count: g.device_count })} size="small" />
                <Chip icon={<AdminPanelSettings sx={{ fontSize: 14 }} />} label={t('deviceGroups.roleCount', { count: g.role_count })} size="small" />
                <Button size="small" onClick={() => setManageGroup(g.id)}>{t('deviceGroups.manageGroup')}</Button>
                <Tooltip title={t('common.delete')}>
                  <IconButton size="small" color="error" onClick={() => { deleteGroup.mutate(g.id) }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Card>
        ))}
      </Stack>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('deviceGroups.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label={t('deviceGroups.groupName')} required value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField label={t('common.description')} value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          <TextField label={t('deviceGroups.color')} type="color" value={form.color}
            onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
            sx={{ '& input': { height: 40, cursor: 'pointer' } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name || createGroup.isPending}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Group Dialog */}
      {manageGroup && (
        <ManageGroupDialog
          groupId={manageGroup}
          devices={devices}
          roles={roles}
          onClose={() => setManageGroup(null)}
        />
      )}
    </Box>
  )
}

function ManageGroupDialog({ groupId, devices, roles, onClose }: {
  groupId: string
  devices: Device[]
  roles: Role[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { data: memberIds = [] } = useGroupDevices(groupId)
  const addDevice = useAddDeviceToGroup()
  const removeDevice = useRemoveDeviceFromGroup()
  const assignRole = useAssignGroupToRole()
  const unassignRole = useUnassignGroupFromRole()
  const { data: assignedRoles = [] } = useGroupRoles(groupId)

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('deviceGroups.manageGroup')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Typography variant="subtitle2">{t('deviceGroups.assignDevices')}</Typography>
        <Autocomplete
          multiple size="small" options={devices}
          getOptionLabel={(d) => d.name || d.device_identifier}
          value={devices.filter((d) => memberIds.includes(d.id))}
          onChange={(_, selected) => {
            const selectedIds = new Set(selected.map((d) => d.id))
            const currentIds = new Set(memberIds)
            // Add new
            for (const id of selectedIds) {
              if (!currentIds.has(id)) addDevice.mutate({ groupId, deviceId: id })
            }
            // Remove deselected
            for (const id of Array.from(currentIds)) {
              if (!selectedIds.has(id as string)) removeDevice.mutate({ groupId, deviceId: id as string })
            }
          }}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
              {option.name}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label={t('deviceGroups.assignDevices')} />}
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('deviceGroups.assignRoles')}</Typography>
        {assignedRoles.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {assignedRoles.map((r: { id: string; name: string }) => (
              <Chip
                key={r.id}
                label={r.name}
                onDelete={() => unassignRole.mutate({ groupId, roleId: r.id })}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>
        )}
        <Autocomplete
          size="small"
          options={roles.filter((r) => !assignedRoles.some((ar: { id: string }) => ar.id === r.id))}
          getOptionLabel={(r) => r.name}
          onChange={(_, role) => {
            if (role) assignRole.mutate({ groupId, roleId: role.id })
          }}
          renderInput={(params) => <TextField {...params} label={t('deviceGroups.selectRole')} />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
